import { useState } from 'react';
import { useLanguage } from '@/contexts/LanguageContext';
import { useClient } from '@/contexts/ClientContext';
import { SidebarLayout, SidebarItem } from '@/components/layout/SidebarLayout';
import { PersonaCardCompact, ChipSelector, ChatWidget, type Persona } from '@/components/ui';

// Window definitions for Research
const windowDefs = [
  {
    id: 'data-sources',
    icon: '🗂️',
    name: 'Data Sources',
    nameEs: 'Fuentes de Datos',
    description: 'Origins & freshness',
    descriptionEs: 'Orígenes y frescura',
    subItems: ['All Sources', 'Syndicated', 'Internal', 'Surveys'],
    subItemsEs: ['Todas', 'Sindicados', 'Internos', 'Encuestas'],
  },
  {
    id: 'research',
    icon: '🔬',
    name: 'Research Tools',
    nameEs: 'Herramientas de Investigación',
    description: 'Analysis & personas',
    descriptionEs: 'Análisis y personas',
    subItems: ['Personas IA', 'Synthetic Data', 'Scenario Testing'],
    subItemsEs: ['Personas IA', 'Datos Sintéticos', 'Prueba de Escenarios'],
  },
  {
    id: 'library',
    icon: '📚',
    name: 'Library',
    nameEs: 'Biblioteca',
    description: 'Saved work',
    descriptionEs: 'Trabajo guardado',
    subItems: ['Saved Scenarios', 'Generated Reports', 'Export Center'],
    subItemsEs: ['Escenarios Guardados', 'Reportes Generados', 'Centro de Exportación'],
  },
  {
    id: 'conversations',
    icon: '💬',
    name: 'Conversations',
    nameEs: 'Conversaciones',
    description: 'Chat history',
    descriptionEs: 'Historial de chat',
    subItems: ['Chat History', 'Request Status'],
    subItemsEs: ['Historial de Chat', 'Estado de Solicitudes'],
  },
];

// Meals de Colombia personas - ice cream/paletas focused
const mealsPersonas: Persona[] = [
  {
    id: 'carlos',
    name: 'Carlos',
    age: 42,
    location: 'Bogota',
    profession: 'Conductor de taxi',
    segment: 'Value Seeker',
    avatar: '👨',
    quote: 'Para refrescarme en el trabajo, nada como una paleta Polet. Rica y a buen precio.',
    metrics: { population: 30, loyalty: 45, frequency: 40 },
    topics: ['Precio', 'Refrescante', 'Tradicional', 'Tiendas'],
    channels: ['Traditional', 'Street Vendors'],
    messageFrame: {
      title: 'Refrescate sin Gastar Mucho',
      message: 'Paletas de calidad a un precio justo para el dia a dia',
    },
    geography: ['Bogota', 'Medellin', 'Cali'],
  },
  {
    id: 'maria',
    name: 'Maria',
    age: 35,
    location: 'Medellin',
    profession: 'Madre de familia',
    segment: 'Family Buyer',
    avatar: '👩',
    quote: 'Los ninos adoran Choco Rico. Siempre tengo en el congelador para cuando llegan del colegio.',
    metrics: { population: 28, loyalty: 55, frequency: 36 },
    topics: ['Ninos', 'Snacks', 'Congelador', 'Supermercado'],
    channels: ['Modern Trade', 'Supermarkets'],
    messageFrame: {
      title: 'Alegria para Toda la Familia',
      message: 'Helados que los ninos aman y los padres aprueban',
    },
    geography: ['Medellin', 'Bogota', 'Barranquilla'],
  },
  {
    id: 'andrea',
    name: 'Andrea',
    age: 28,
    location: 'Cartagena',
    profession: 'Ejecutiva de ventas',
    segment: 'Premium Indulgent',
    avatar: '👩‍💼',
    quote: 'Despues de un dia largo, me doy el gusto con un helado cremoso de calidad.',
    metrics: { population: 18, loyalty: 50, frequency: 24 },
    topics: ['Premium', 'Cremoso', 'Indulgencia', 'Calidad'],
    channels: ['Modern Trade', 'Convenience', 'E-commerce'],
    messageFrame: {
      title: 'Te Mereces lo Mejor',
      message: 'Helados premium para momentos de verdadero placer',
    },
    geography: ['Cartagena', 'Bogota', 'Santa Marta'],
  },
  {
    id: 'santiago',
    name: 'Santiago',
    age: 22,
    location: 'Cali',
    profession: 'Estudiante universitario',
    segment: 'Young & Impulsive',
    avatar: '👨‍🎓',
    quote: 'Cuando hace calor, paso por la tienda y agarro una paleta. Simple y refrescante.',
    metrics: { population: 15, loyalty: 30, frequency: 48 },
    topics: ['Impulso', 'Refrescante', 'Sabores', 'Tienda'],
    channels: ['Traditional', 'Convenience', 'Street Vendors'],
    messageFrame: {
      title: 'El Antojo Perfecto',
      message: 'Para esos momentos cuando necesitas algo dulce y frio',
    },
    geography: ['Cali', 'Bucaramanga', 'Pereira'],
  },
  {
    id: 'isabel',
    name: 'Isabel',
    age: 45,
    location: 'Barranquilla',
    profession: 'Duena de tienda',
    segment: 'Traditional Loyalist',
    avatar: '👩‍🦰',
    quote: 'Siempre he vendido Polet en mi tienda. Mis clientes confian en la marca.',
    metrics: { population: 9, loyalty: 70, frequency: 20 },
    topics: ['Tradicion', 'Confianza', 'Marca', 'Tienda'],
    channels: ['Traditional'],
    messageFrame: {
      title: 'La Marca que Conoces',
      message: 'Generaciones de colombianos han crecido con nuestros helados',
    },
    geography: ['Barranquilla', 'Costa Atlantica'],
  },
];

// Generic personas for other clients (neutral industry)
const genericPersonas: Persona[] = [
  {
    id: 'consumer1',
    name: 'Consumer A',
    age: 35,
    location: 'Urban Center',
    profession: 'Professional',
    segment: 'Value Seeker',
    avatar: '👤',
    quote: 'I look for good quality at a reasonable price.',
    metrics: { population: 30, loyalty: 45, frequency: 40 },
    topics: ['Value', 'Quality', 'Convenience'],
    channels: ['Modern Trade', 'Traditional'],
    messageFrame: {
      title: 'Quality at Fair Price',
      message: 'Products that deliver value without compromise',
    },
    geography: ['Metro Areas'],
  },
  {
    id: 'consumer2',
    name: 'Consumer B',
    age: 42,
    location: 'Suburban',
    profession: 'Family Provider',
    segment: 'Family Buyer',
    avatar: '👥',
    quote: 'I buy for the whole family, so quantity and value matter.',
    metrics: { population: 28, loyalty: 55, frequency: 36 },
    topics: ['Family', 'Bulk', 'Savings'],
    channels: ['Supermarkets', 'Hypermarkets'],
    messageFrame: {
      title: 'For the Whole Family',
      message: 'Products that satisfy everyone at home',
    },
    geography: ['Suburbs', 'Secondary Cities'],
  },
  {
    id: 'consumer3',
    name: 'Consumer C',
    age: 28,
    location: 'Urban',
    profession: 'Young Professional',
    segment: 'Premium Seeker',
    avatar: '👤',
    quote: 'I am willing to pay more for premium quality.',
    metrics: { population: 18, loyalty: 50, frequency: 24 },
    topics: ['Premium', 'Quality', 'Experience'],
    channels: ['Modern Trade', 'E-commerce'],
    messageFrame: {
      title: 'Premium Experience',
      message: 'For those who appreciate the finer things',
    },
    geography: ['Major Cities'],
  },
  {
    id: 'consumer4',
    name: 'Consumer D',
    age: 24,
    location: 'Urban',
    profession: 'Student',
    segment: 'Impulse Buyer',
    avatar: '👤',
    quote: 'I buy on impulse when I see something I like.',
    metrics: { population: 15, loyalty: 30, frequency: 48 },
    topics: ['Impulse', 'Variety', 'New Products'],
    channels: ['Convenience', 'Traditional'],
    messageFrame: {
      title: 'Try Something New',
      message: 'Exciting products for spontaneous moments',
    },
    geography: ['Urban Centers'],
  },
  {
    id: 'consumer5',
    name: 'Consumer E',
    age: 55,
    location: 'Traditional',
    profession: 'Established',
    segment: 'Loyal Traditional',
    avatar: '👤',
    quote: 'I stick with brands I know and trust.',
    metrics: { population: 9, loyalty: 70, frequency: 20 },
    topics: ['Trust', 'Tradition', 'Reliability'],
    channels: ['Traditional'],
    messageFrame: {
      title: 'Trusted Quality',
      message: 'The brands you have always relied on',
    },
    geography: ['All Regions'],
  },
];

// Function to get personas based on client
function getPersonasForClient(clientId: string | null): Persona[] {
  if (!clientId) return genericPersonas;

  // Meals de Colombia
  if (clientId.toLowerCase().includes('meals')) {
    return mealsPersonas;
  }

  // Default to generic for unknown clients
  return genericPersonas;
}

export function RequestPage() {
  const [activeWindow, setActiveWindow] = useState<string>('research');
  const [activeTab, setActiveTab] = useState<string>('Personas IA');
  const { t, language } = useLanguage();
  const { selectedClient } = useClient();

  // Build sidebar items with language support
  const sidebarItems: SidebarItem[] = windowDefs.map((w) => ({
    id: w.id,
    icon: <span className="text-lg">{w.icon}</span>,
    name: language === 'es' ? w.nameEs : w.name,
    description: language === 'es' ? w.descriptionEs : w.description,
    subItems: (language === 'es' ? w.subItemsEs : w.subItems).map((si, i) => ({
      id: w.subItems[i], // Keep English ID for routing
      label: si,
    })),
  }));

  const selectedWindow = windowDefs.find((w) => w.id === activeWindow);

  // When window changes, reset to first tab
  const handleWindowChange = (windowId: string) => {
    setActiveWindow(windowId);
    const win = windowDefs.find((w) => w.id === windowId);
    if (win) {
      setActiveTab(win.subItems[0]);
    }
  };

  const getTabLabel = (tabId: string) => {
    if (language !== 'es') return tabId;
    const win = windowDefs.find((w) => w.id === activeWindow);
    if (!win) return tabId;
    const idx = win.subItems.indexOf(tabId);
    return idx >= 0 ? win.subItemsEs[idx] : tabId;
  };

  const contentHeader = selectedWindow && (
    <div className="flex items-center gap-3 mb-6">
      <span className="text-2xl">{selectedWindow.icon}</span>
      <div>
        <h2 className="text-xl font-semibold text-white">
          {language === 'es' ? selectedWindow.nameEs : selectedWindow.name}
        </h2>
        <p className="text-sm text-text-secondary">{getTabLabel(activeTab)}</p>
      </div>
    </div>
  );

  return (
    <SidebarLayout
      title={t.research}
      subtitle={language === 'es' ? 'Investigación, trabajo guardado e historial' : 'Research, saved work, and conversation history'}
      items={sidebarItems}
      activeItemId={activeWindow}
      activeSubItemId={activeTab}
      onItemChange={handleWindowChange}
      onSubItemChange={setActiveTab}
      contentHeader={contentHeader}
    >
      <ResearchPageContent
        windowId={activeWindow}
        tab={activeTab}
        clientId={selectedClient?.client_id || null}
        language={language}
      />
    </SidebarLayout>
  );
}

function ResearchPageContent({
  windowId,
  tab,
  clientId,
  language,
}: {
  windowId: string;
  tab: string;
  clientId: string | null;
  language: string;
}) {
  if (windowId === 'data-sources') {
    return <DataSourcesContent tab={tab} language={language} />;
  }
  if (windowId === 'research') {
    return <ResearchContent tab={tab} clientId={clientId} language={language} />;
  }
  if (windowId === 'library') {
    return <LibraryContent tab={tab} language={language} />;
  }
  if (windowId === 'conversations') {
    return <ConversationsContent tab={tab} language={language} clientId={clientId} />;
  }
  return null;
}

function ResearchContent({ tab, clientId, language }: { tab: string; clientId: string | null; language: string }) {
  const [selectedPersona, setSelectedPersona] = useState<Persona | null>(null);
  const [selectedSegments, setSelectedSegments] = useState<string[]>([]);
  const [studyName, setStudyName] = useState('');
  const [questions, setQuestions] = useState('');

  // Get personas based on selected client
  const personas = getPersonasForClient(clientId);
  const segments = [...new Set(personas.map(p => p.segment))];

  const handleChat = (persona: Persona) => {
    setSelectedPersona(persona);
    // In a real app, this would open a chat interface
    console.log('Starting chat with:', persona.name);
  };

  if (tab === 'Personas IA') {
    return (
      <div className="space-y-6">
        {/* Header */}
        <div className="card p-6">
          <div className="flex items-center justify-between mb-4">
            <div>
              <h3 className="text-lg font-semibold text-white">
                {language === 'es' ? 'Personas IA Disponibles' : 'Available AI Personas'}
              </h3>
              <p className="text-sm text-text-secondary">
                {language === 'es'
                  ? 'Selecciona una persona para iniciar una conversación simulada'
                  : 'Select a persona to start a simulated conversation'}
              </p>
            </div>
            <div className="flex items-center gap-2">
              <span className="text-sm text-text-secondary">
                {language === 'es' ? 'Filtrar:' : 'Filter:'}
              </span>
              <select className="bg-surface-600 border border-surface-400/30 rounded px-2 py-1 text-sm text-white">
                <option>{language === 'es' ? 'Todos' : 'All'} ({personas.length})</option>
                {segments.map(seg => (
                  <option key={seg}>{seg}</option>
                ))}
              </select>
            </div>
          </div>

          {!clientId && (
            <div className="p-4 bg-warning-500/10 border border-warning-500/20 rounded-lg mb-4">
              <p className="text-sm text-warning-400">
                {language === 'es'
                  ? '⚠️ Selecciona un cliente para personalizar las personas según su mercado'
                  : '⚠️ Select a client to customize personas for their market'}
              </p>
            </div>
          )}
        </div>

        {/* Persona Grid - VoterPulse style */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
          {personas.map((persona) => (
            <PersonaCardCompact
              key={persona.id}
              persona={persona}
              onChat={clientId ? handleChat : undefined}
            />
          ))}
        </div>

        {/* Chat Modal with Real ChatWidget */}
        {selectedPersona && (
          <div className="fixed inset-0 bg-black/60 flex items-center justify-center z-50 p-4">
            <div className="w-full max-w-2xl h-[600px] max-h-[80vh]">
              <ChatWidget
                clientId={clientId || undefined}
                persona={selectedPersona}
                onClose={() => setSelectedPersona(null)}
                language={language}
              />
            </div>
          </div>
        )}
      </div>
    );
  }

  if (tab === 'Synthetic Data') {
    const segmentOptions = [
      { id: 'health', label: 'Health-Conscious' },
      { id: 'price', label: 'Price-Sensitive' },
      { id: 'convenience', label: 'Convenience' },
      { id: 'traditional', label: 'Traditional' },
    ];

    return (
      <div className="space-y-6">
        {/* Delivery Times Info */}
        <div className="card p-4 bg-primary-500/10 border-primary-500/20">
          <div className="flex items-center gap-3">
            <span className="text-xl">⏰</span>
            <div>
              <h4 className="text-sm font-medium text-primary-300">
                {language === 'es' ? 'Tiempos de Entrega' : 'Delivery Times'}
              </h4>
              <p className="text-xs text-text-secondary">
                {language === 'es'
                  ? 'Lun 6PM → Jue 8AM → Jue 6PM → Lun 8AM'
                  : 'Mon 6PM → Thu 8AM → Thu 6PM → Mon 8AM'}
              </p>
            </div>
          </div>
        </div>

        {/* Request Form */}
        <div className="card p-6">
          <h3 className="text-lg font-semibold text-white mb-4">
            {language === 'es' ? 'Solicitar Simulación de Encuesta' : 'Request Survey Simulation'}
          </h3>
          <p className="text-sm text-text-secondary mb-6">
            {language === 'es'
              ? 'Nuestro equipo generará datos sintéticos basados en tus especificaciones'
              : 'Our team will generate synthetic data based on your specifications'}
          </p>

          <div className="space-y-6">
            {/* Study Name */}
            <div>
              <label className="block text-sm font-medium text-text-secondary mb-2">
                {language === 'es' ? 'Nombre del Estudio' : 'Study Name'} *
              </label>
              <input
                type="text"
                value={studyName}
                onChange={(e) => setStudyName(e.target.value)}
                placeholder={language === 'es' ? 'Ej: Encuesta Yogurt Colombia Q1 2026' : 'E.g.: Yogurt Survey Colombia Q1 2026'}
                className="w-full bg-surface-800 border border-surface-400/30 rounded-lg px-4 py-2.5 text-white text-sm placeholder:text-text-tertiary focus:border-primary-500 focus:ring-1 focus:ring-primary-500 transition-all"
              />
            </div>

            {/* Target Segments */}
            <ChipSelector
              label={language === 'es' ? 'Segmentos Objetivo *' : 'Target Segments *'}
              options={segmentOptions}
              selected={selectedSegments}
              onChange={setSelectedSegments}
              multiSelect={true}
            />

            {/* Key Questions */}
            <div>
              <label className="block text-sm font-medium text-text-secondary mb-2">
                {language === 'es' ? 'Preguntas Clave (opcional)' : 'Key Questions (optional)'}
              </label>
              <textarea
                value={questions}
                onChange={(e) => setQuestions(e.target.value)}
                placeholder={language === 'es'
                  ? '1. ¿Cuál es su opinión sobre...?\n2. ¿Cómo valora...?\n3. ¿Qué importancia tiene...?'
                  : '1. What is your opinion on...?\n2. How do you rate...?\n3. How important is...?'}
                className="w-full bg-surface-800 border border-surface-400/30 rounded-lg px-4 py-3 text-white text-sm h-28 resize-none placeholder:text-text-tertiary focus:border-primary-500 focus:ring-1 focus:ring-primary-500 transition-all"
              />
              <p className="text-xs text-text-tertiary mt-1">
                {language === 'es' ? 'Máximo 5 preguntas por solicitud' : 'Maximum 5 questions per request'}
              </p>
            </div>

            {/* Submit Button */}
            <button
              className="w-full py-3 bg-primary-500 hover:bg-primary-400 text-white font-medium rounded-lg transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
              disabled={!studyName || selectedSegments.length === 0}
            >
              {language === 'es' ? 'Enviar Solicitud' : 'Submit Request'}
            </button>
          </div>
        </div>
      </div>
    );
  }

  if (tab === 'Scenario Testing') {
    const scenarios = [
      {
        title: language === 'es' ? 'Umbral de Precio' : 'Price Threshold Test',
        description: language === 'es'
          ? '¿Qué precio causa caída significativa en demanda?'
          : 'What price point causes significant demand drop?',
        icon: '💰',
        step: 1,
      },
      {
        title: language === 'es' ? 'Migración de Segmentos' : 'Segment Migration',
        description: language === 'es'
          ? '¿Los consumidores están migrando entre segmentos?'
          : 'Are consumers moving between segments over time?',
        icon: '📊',
        step: 2,
      },
      {
        title: language === 'es' ? 'Cambio de Canal' : 'Channel Shift',
        description: language === 'es'
          ? '¿E-commerce está ganando share del canal tradicional?'
          : 'Is e-commerce gaining share from traditional retail?',
        icon: '🛒',
        step: 3,
      },
    ];

    return (
      <div className="space-y-6">
        <div className="card p-6">
          <div className="flex items-center gap-3 mb-4">
            <div className="w-12 h-12 rounded-xl bg-accent-purple/20 flex items-center justify-center text-2xl">
              🎯
            </div>
            <div>
              <h3 className="text-lg font-semibold text-white">
                {language === 'es' ? 'Prueba de Escenarios' : 'Scenario Testing'}
              </h3>
              <p className="text-sm text-text-secondary">
                {language === 'es'
                  ? 'Prueba escenarios de negocio con simulaciones basadas en datos'
                  : 'Test business scenarios with data-driven simulations'}
              </p>
            </div>
          </div>

          {/* Scenario flow indicator */}
          <div className="flex items-center justify-center gap-2 mb-6 text-xs text-text-tertiary">
            <span className="flex items-center gap-1.5">
              <span className="w-5 h-5 rounded-full bg-primary-500/20 text-primary-400 flex items-center justify-center text-[10px] font-bold">1</span>
              {language === 'es' ? 'Seleccionar tipo' : 'Select type'}
            </span>
            <span className="w-4 h-px bg-surface-500" />
            <span className="flex items-center gap-1.5">
              <span className="w-5 h-5 rounded-full bg-surface-600 text-text-secondary flex items-center justify-center text-[10px] font-bold">2</span>
              {language === 'es' ? 'Configurar' : 'Configure'}
            </span>
            <span className="w-4 h-px bg-surface-500" />
            <span className="flex items-center gap-1.5">
              <span className="w-5 h-5 rounded-full bg-surface-600 text-text-secondary flex items-center justify-center text-[10px] font-bold">3</span>
              {language === 'es' ? 'Ejecutar' : 'Run'}
            </span>
            <span className="w-4 h-px bg-surface-500" />
            <span className="flex items-center gap-1.5">
              <span className="w-5 h-5 rounded-full bg-surface-600 text-text-secondary flex items-center justify-center text-[10px] font-bold">4</span>
              {language === 'es' ? 'Revisar' : 'Review'}
            </span>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            {scenarios.map((s, i) => (
              <button
                key={i}
                className="p-4 bg-surface-600/50 border border-surface-400/20 rounded-lg text-left hover:bg-surface-600 hover:border-primary-500/30 transition-all group"
              >
                <span className="text-2xl mb-2 block">{s.icon}</span>
                <h4 className="text-sm font-medium text-white group-hover:text-primary-300 transition-colors">
                  {s.title}
                </h4>
                <p className="text-xs text-text-tertiary mt-1">{s.description}</p>
              </button>
            ))}
          </div>
        </div>

        {/* Custom Scenario */}
        <div className="card p-6">
          <h4 className="font-medium text-white mb-4">
            {language === 'es' ? 'Escenario Personalizado' : 'Custom Scenario'}
          </h4>
          <textarea
            className="w-full bg-surface-800 border border-surface-400/30 rounded-lg px-4 py-3 text-white text-sm h-24 resize-none mb-4 placeholder:text-text-tertiary focus:border-primary-500 focus:ring-1 focus:ring-primary-500 transition-all"
            placeholder={language === 'es'
              ? 'Describe tu escenario...\nEj: Si bajamos el precio 10%, ¿el volumen aumentará 15%?'
              : 'Describe your scenario...\nE.g.: If we reduce price by 10%, will volume increase by 15%?'}
          />
          <button className="btn btn-secondary">
            {language === 'es' ? 'Ejecutar Escenario' : 'Run Scenario'}
          </button>
        </div>
      </div>
    );
  }

  return null;
}

function LibraryContent({ tab, language }: { tab: string; language: string }) {
  if (tab === 'Saved Scenarios') {
    return (
      <div className="card p-6">
        <div className="flex items-center justify-between mb-4">
          <h3 className="text-lg font-semibold text-white">
            {language === 'es' ? 'Escenarios Guardados' : 'Saved Scenarios'}
          </h3>
          <span className="text-xs text-text-tertiary bg-surface-600 px-2 py-1 rounded">3 {language === 'es' ? 'guardados' : 'saved'}</span>
        </div>
        <div className="space-y-3">
          <LibraryItem
            title={language === 'es' ? 'Plan Lanzamiento Q1 2026' : 'Q1 2026 Launch Plan'}
            subtitle={`${language === 'es' ? 'Planificación de Lanzamiento' : 'Launch Planning'} • Jan 15`}
            status="active"
            statusLabel={language === 'es' ? 'activo' : 'active'}
          />
          <LibraryItem
            title={language === 'es' ? 'Impacto Aumento de Precio' : 'Price Increase Impact'}
            subtitle={`${language === 'es' ? 'Optimización' : 'Optimization'} • Jan 10`}
            status="archived"
            statusLabel={language === 'es' ? 'archivado' : 'archived'}
          />
          <LibraryItem
            title={language === 'es' ? 'Racionalización Portafolio' : 'Portfolio Rationalization'}
            subtitle={`${language === 'es' ? 'Estrategia' : 'Strategy'} • Dec 28`}
            status="archived"
            statusLabel={language === 'es' ? 'archivado' : 'archived'}
          />
        </div>
      </div>
    );
  }

  if (tab === 'Generated Reports') {
    return (
      <div className="card p-6">
        <div className="flex items-center justify-between mb-4">
          <h3 className="text-lg font-semibold text-white">
            {language === 'es' ? 'Reportes Generados' : 'Generated Reports'}
          </h3>
          <span className="text-xs text-text-tertiary bg-surface-600 px-2 py-1 rounded">5 {language === 'es' ? 'reportes' : 'reports'}</span>
        </div>
        <div className="space-y-3">
          <LibraryItem title={language === 'es' ? 'Resumen Mercado Q4' : 'Market Overview Q4'} subtitle="PDF • 2.3 MB" icon="📄" />
          <LibraryItem title={language === 'es' ? 'Análisis Competitivo' : 'Competitive Analysis'} subtitle="PPTX • 5.1 MB" icon="📊" />
          <LibraryItem title={language === 'es' ? 'Profundización Segmentos' : 'Segment Deep Dive'} subtitle="XLSX • 1.8 MB" icon="📈" />
        </div>
      </div>
    );
  }

  if (tab === 'Export Center') {
    return (
      <div className="card p-6">
        <h3 className="text-lg font-semibold text-white mb-4">
          {language === 'es' ? 'Centro de Exportación' : 'Export Center'}
        </h3>
        <p className="text-sm text-text-secondary mb-4">
          {language === 'es' ? 'Exporta tus análisis y datos' : 'Export your analyses and data'}
        </p>
        <div className="space-y-2">
          <ExportButton format="PowerPoint" icon="📊" description={language === 'es' ? 'Presentación' : 'Presentation slides'} />
          <ExportButton format="Excel" icon="📈" description={language === 'es' ? 'Tablas de datos' : 'Data tables'} />
          <ExportButton format="PDF" icon="📄" description={language === 'es' ? 'Documento de reporte' : 'Report document'} />
        </div>
      </div>
    );
  }

  return null;
}

function LibraryItem({ title, subtitle, status, statusLabel, icon }: { title: string; subtitle: string; status?: string; statusLabel?: string; icon?: string }) {
  return (
    <div className="p-3 bg-surface-600/50 rounded-lg hover:bg-surface-600 transition-colors cursor-pointer group">
      <div className="flex items-center gap-3">
        {icon && <span className="text-lg">{icon}</span>}
        <div className="flex-1 min-w-0">
          <div className="text-sm text-white truncate group-hover:text-primary-300">{title}</div>
          <div className="text-xs text-text-tertiary">{subtitle}</div>
        </div>
        {status && (
          <span className={`text-xs px-2 py-0.5 rounded ${
            status === 'active' ? 'bg-success-500/20 text-success-400' : 'bg-surface-500 text-text-secondary'
          }`}>
            {statusLabel || status}
          </span>
        )}
      </div>
    </div>
  );
}

function ExportButton({ format, icon, description }: { format: string; icon: string; description: string }) {
  return (
    <button className="w-full p-3 bg-surface-600/50 border border-surface-400/20 rounded-lg text-left hover:bg-surface-600 hover:border-primary-500/30 transition-all flex items-center gap-3 group">
      <span className="text-xl">{icon}</span>
      <div>
        <div className="text-sm font-medium text-white group-hover:text-primary-300">{format}</div>
        <div className="text-xs text-text-tertiary">{description}</div>
      </div>
    </button>
  );
}

function ConversationsContent({ tab, language, clientId }: { tab: string; language: string; clientId: string | null }) {
  const [showChat, setShowChat] = useState(false);

  if (tab === 'Chat History') {
    return (
      <>
        <div className="space-y-4">
          {/* New Chat Button */}
          <button
            onClick={() => setShowChat(true)}
            className="w-full card p-4 hover-glow flex items-center justify-center gap-3 group"
          >
            <div className="w-10 h-10 rounded-lg bg-primary-500/20 flex items-center justify-center">
              <svg className="w-5 h-5 text-primary-400" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4v16m8-8H4" />
              </svg>
            </div>
            <span className="text-white font-medium group-hover:text-primary-300 transition-colors">
              {language === 'es' ? 'Nueva Conversación' : 'New Conversation'}
            </span>
          </button>

          {/* Chat History */}
          <div className="card p-6">
            <h3 className="text-lg font-semibold text-white mb-4">
              {language === 'es' ? 'Historial de Chat' : 'Chat History'}
            </h3>
            <div className="space-y-3">
              <ChatHistoryItem
                title={language === 'es' ? 'Desglose de tamaño de mercado por segmento' : 'Market size breakdown by segment'}
                timestamp={language === 'es' ? 'Hoy, 2:30 PM' : 'Today, 2:30 PM'}
                preview={language === 'es' ? 'El mercado se segmenta en 4 grupos principales...' : 'The market is segmented into 4 main groups...'}
              />
              <ChatHistoryItem
                title={language === 'es' ? 'Elasticidad de precio para Yogurt Griego' : 'Price elasticity for Yogurt Griego'}
                timestamp={language === 'es' ? 'Hoy, 11:15 AM' : 'Today, 11:15 AM'}
                preview={language === 'es' ? 'El producto muestra elasticidad de -1.2...' : 'The product shows elasticity of -1.2...'}
              />
              <ChatHistoryItem
                title={language === 'es' ? 'Análisis competitivo para Alpina' : 'Competitor analysis for Alpina'}
                timestamp={language === 'es' ? 'Ayer' : 'Yesterday'}
                preview={language === 'es' ? 'Las principales fortalezas de Alpina incluyen...' : "Alpina's main strengths include..."}
              />
            </div>
          </div>
        </div>

        {/* General Chat Modal */}
        {showChat && (
          <div className="fixed inset-0 bg-black/60 flex items-center justify-center z-50 p-4">
            <div className="w-full max-w-2xl h-[600px] max-h-[80vh]">
              <ChatWidget
                clientId={clientId || undefined}
                onClose={() => setShowChat(false)}
                language={language}
              />
            </div>
          </div>
        )}
      </>
    );
  }

  if (tab === 'Request Status') {
    return (
      <div className="card p-6">
        <h3 className="text-lg font-semibold text-white mb-4">
          {language === 'es' ? 'Estado de Solicitudes' : 'Request Status'}
        </h3>
        <div className="space-y-3">
          <RequestItem
            title={language === 'es' ? 'Profundización de canales' : 'Channel deep dive'}
            status="completed"
            timestamp={language === 'es' ? 'Completado Ene 22' : 'Completed Jan 22'}
            language={language}
          />
          <RequestItem
            title={language === 'es' ? 'Conversaciones persona (Salud)' : 'Persona conversations (Health)'}
            status="in_progress"
            timestamp={language === 'es' ? 'Procesando...' : 'Processing...'}
            language={language}
          />
          <RequestItem
            title={language === 'es' ? 'Modelo optimización de precios' : 'Price optimization model'}
            status="queued"
            timestamp={language === 'es' ? 'En cola' : 'Queued'}
            language={language}
          />
        </div>
      </div>
    );
  }

  return null;
}

function ChatHistoryItem({ title, timestamp, preview }: { title: string; timestamp: string; preview: string }) {
  return (
    <div className="p-4 bg-surface-600/50 rounded-lg hover:bg-surface-600 transition-colors cursor-pointer group">
      <div className="flex items-start justify-between gap-4">
        <div className="flex-1 min-w-0">
          <div className="text-sm font-medium text-white group-hover:text-primary-300 truncate">{title}</div>
          <div className="text-xs text-text-tertiary mt-1 line-clamp-2">{preview}</div>
        </div>
        <div className="text-xs text-text-tertiary whitespace-nowrap">{timestamp}</div>
      </div>
    </div>
  );
}

function RequestItem({ title, status, timestamp, language }: { title: string; status: string; timestamp: string; language: string }) {
  const statusStyles = {
    completed: 'bg-success-500/20 text-success-400',
    in_progress: 'bg-primary-500/20 text-primary-400',
    queued: 'bg-surface-500 text-text-secondary',
  };

  const statusLabels = {
    completed: language === 'es' ? 'Listo' : 'Done',
    in_progress: language === 'es' ? 'Procesando' : 'Running',
    queued: language === 'es' ? 'En Cola' : 'Queued',
  };

  return (
    <div className="p-3 bg-surface-600/50 rounded-lg">
      <div className="flex items-center justify-between gap-2">
        <div className="text-sm text-white truncate">{title}</div>
        <span className={`text-xs px-2 py-0.5 rounded whitespace-nowrap ${statusStyles[status as keyof typeof statusStyles]}`}>
          {statusLabels[status as keyof typeof statusLabels]}
        </span>
      </div>
      <div className="text-xs text-text-tertiary mt-1">{timestamp}</div>
    </div>
  );
}
// Data Sources Content
function DataSourcesContent({ tab, language }: { tab: string; language: string }) {
  const dataSources = [
    {
      id: 'nielsen',
      name: 'Nielsen Retail Audit',
      nameEs: 'Auditoria Retail Nielsen',
      type: 'Syndicated',
      typeEs: 'Sindicados',
      icon: '📊',
      freshness: 'weekly',
      lastUpdate: '2026-01-30',
      coverage: '85%',
      metrics: ['Volume Sales', 'Value Sales', 'Distribution', 'Price'],
      metricsEs: ['Ventas Volumen', 'Ventas Valor', 'Distribucion', 'Precio'],
    },
    {
      id: 'euromonitor',
      name: 'Euromonitor Passport',
      nameEs: 'Euromonitor Passport',
      type: 'Syndicated',
      typeEs: 'Sindicados',
      icon: '🌍',
      freshness: 'quarterly',
      lastUpdate: '2026-01-15',
      coverage: '100%',
      metrics: ['Market Size', 'Growth', 'Forecast', 'Competitive Share'],
      metricsEs: ['Tamano Mercado', 'Crecimiento', 'Pronostico', 'Share Competitivo'],
    },
    {
      id: 'internal-sales',
      name: 'Internal Sales Data',
      nameEs: 'Datos Ventas Internas',
      type: 'Internal',
      typeEs: 'Internos',
      icon: '📈',
      freshness: 'daily',
      lastUpdate: '2026-02-05',
      coverage: '100%',
      metrics: ['SKU Sales', 'Channel Mix', 'Regional Sales', 'Promotions'],
      metricsEs: ['Ventas SKU', 'Mix Canal', 'Ventas Regional', 'Promociones'],
    },
    {
      id: 'consumer-survey',
      name: 'Consumer Survey Panel',
      nameEs: 'Panel Encuesta Consumidor',
      type: 'Surveys',
      typeEs: 'Encuestas',
      icon: '👥',
      freshness: 'monthly',
      lastUpdate: '2026-01-20',
      coverage: 'n=1,500',
      metrics: ['Awareness', 'Trial', 'Repeat', 'NPS', 'Preferences'],
      metricsEs: ['Conocimiento', 'Prueba', 'Recompra', 'NPS', 'Preferencias'],
    },
    {
      id: 'pos-data',
      name: 'Point of Sale Data',
      nameEs: 'Datos Punto de Venta',
      type: 'Internal',
      typeEs: 'Internos',
      icon: '🛒',
      freshness: 'weekly',
      lastUpdate: '2026-02-01',
      coverage: '65%',
      metrics: ['Transactions', 'Basket Size', 'Time of Purchase', 'Store Type'],
      metricsEs: ['Transacciones', 'Tamano Canasta', 'Hora Compra', 'Tipo Tienda'],
    },
    {
      id: 'social-listening',
      name: 'Social Listening',
      nameEs: 'Escucha Social',
      type: 'Syndicated',
      typeEs: 'Sindicados',
      icon: '📱',
      freshness: 'daily',
      lastUpdate: '2026-02-05',
      coverage: '78%',
      metrics: ['Mentions', 'Sentiment', 'Share of Voice', 'Trends'],
      metricsEs: ['Menciones', 'Sentimiento', 'Share de Voz', 'Tendencias'],
    },
  ];

  const freshnessConfig: Record<string, { label: string; labelEs: string; color: string }> = {
    daily: { label: 'Daily', labelEs: 'Diario', color: 'text-green-400' },
    weekly: { label: 'Weekly', labelEs: 'Semanal', color: 'text-cyan-400' },
    monthly: { label: 'Monthly', labelEs: 'Mensual', color: 'text-amber-400' },
    quarterly: { label: 'Quarterly', labelEs: 'Trimestral', color: 'text-orange-400' },
  };

  // Filter by tab
  const filteredSources = tab === 'All Sources' || tab === 'Todas'
    ? dataSources
    : dataSources.filter(s => {
        const typeMatch = language === 'es' ? s.typeEs : s.type;
        return typeMatch === tab || s.type === tab;
      });

  const formatDate = (dateStr: string) => {
    const date = new Date(dateStr);
    return date.toLocaleDateString(language === 'es' ? 'es-MX' : 'en-US', {
      month: 'short',
      day: 'numeric',
      year: 'numeric',
    });
  };

  return (
    <div className="space-y-6">
      {/* Summary Stats */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
        <div className="card p-4">
          <div className="text-2xl font-bold text-white">{dataSources.length}</div>
          <div className="text-sm text-text-secondary">
            {language === 'es' ? 'Fuentes Activas' : 'Active Sources'}
          </div>
        </div>
        <div className="card p-4">
          <div className="text-2xl font-bold text-green-400">3</div>
          <div className="text-sm text-text-secondary">
            {language === 'es' ? 'Actualizacion Diaria' : 'Daily Updates'}
          </div>
        </div>
        <div className="card p-4">
          <div className="text-2xl font-bold text-cyan-400">85%</div>
          <div className="text-sm text-text-secondary">
            {language === 'es' ? 'Cobertura Promedio' : 'Avg Coverage'}
          </div>
        </div>
        <div className="card p-4">
          <div className="text-2xl font-bold text-primary-400">24</div>
          <div className="text-sm text-text-secondary">
            {language === 'es' ? 'Metricas Disponibles' : 'Available Metrics'}
          </div>
        </div>
      </div>

      {/* Sources Grid */}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        {filteredSources.map((source) => (
          <div key={source.id} className="card p-5 hover-glow">
            <div className="flex items-start gap-4">
              <div className="w-12 h-12 rounded-xl bg-surface-600 flex items-center justify-center text-2xl">
                {source.icon}
              </div>
              <div className="flex-1 min-w-0">
                <div className="flex items-center gap-2 mb-1">
                  <h4 className="font-semibold text-white truncate">
                    {language === 'es' ? source.nameEs : source.name}
                  </h4>
                  <span className="text-xs bg-surface-600 text-text-secondary px-2 py-0.5 rounded">
                    {language === 'es' ? source.typeEs : source.type}
                  </span>
                </div>

                {/* Freshness & Update */}
                <div className="flex items-center gap-3 text-sm mb-3">
                  <span className={`${freshnessConfig[source.freshness].color}`}>
                    ● {language === 'es'
                      ? freshnessConfig[source.freshness].labelEs
                      : freshnessConfig[source.freshness].label}
                  </span>
                  <span className="text-text-tertiary">
                    {language === 'es' ? 'Actualizado' : 'Updated'}: {formatDate(source.lastUpdate)}
                  </span>
                </div>

                {/* Coverage */}
                <div className="text-xs text-text-secondary mb-2">
                  {language === 'es' ? 'Cobertura' : 'Coverage'}:
                  <span className="text-white ml-1">{source.coverage}</span>
                </div>

                {/* Metrics */}
                <div className="flex flex-wrap gap-1.5">
                  {(language === 'es' ? source.metricsEs : source.metrics).map((metric, i) => (
                    <span
                      key={i}
                      className="text-xs bg-surface-700 text-text-secondary px-2 py-0.5 rounded"
                    >
                      {metric}
                    </span>
                  ))}
                </div>
              </div>
            </div>
          </div>
        ))}
      </div>

      {/* Info Box */}
      <div className="card p-4 bg-primary-500/5 border-primary-500/20">
        <div className="flex items-start gap-3">
          <span className="text-xl">💡</span>
          <div>
            <h4 className="text-sm font-medium text-primary-300 mb-1">
              {language === 'es' ? 'Integracion de Datos' : 'Data Integration'}
            </h4>
            <p className="text-sm text-text-secondary">
              {language === 'es'
                ? 'Todas las fuentes de datos se integran automaticamente en los modelos de simulacion y analisis de mercado.'
                : 'All data sources are automatically integrated into simulation models and market analysis.'}
            </p>
          </div>
        </div>
      </div>
    </div>
  );
}

